<script setup>
  import { onMounted, ref } from 'vue'
  import { useRouter } from 'vue-router'
  import axios from 'axios'
  import { marked } from 'marked'
  import EditDialog from '@/components/EditDialog.vue'
  import { Consts } from '@/components/logics/Consts'
  import { validateCorporateNumber, convertToHalfWidthNumber, convertToFullWidth } from '@/components/logics/Util'
  import { useJoinStore } from '@/stores/joinStore'
  import { useDialogStore } from '@/stores/dialogStore'
  import { useRegionStore } from '@/stores/regionStore'
  import FooterNoneLogin from '@/components/FooterNoneLogin.vue'

  const router = useRouter()

  // pinia
  const joinStore = useJoinStore()
  const dialogStore = useDialogStore()
  const regionStore = useRegionStore()

  // dialog
  const showPopupTerms = ref(false)

  // 利用規約
  const consts = Consts()
  const termsValue = ref('')

  // 入力ハンドラ /////////////////////////////////////////

  // 法人番号入力時の処理
  const handleCorporateNumberInput = event => {
    // validateCorporateNumberの戻り値を使って更新
    joinStore.formData.corporateNumber = validateCorporateNumber(event)
  }

  // 全角文字入力の処理
  const handleFullWidthInput = (event, key) => {
    joinStore.formData[key] = convertToFullWidth(event.target.value)
  }

  // 電話番号入力時の処理
  const handlePhoneInput = (event, index) => {
    // 全角を半角に変換
    const value = event.target.value
    const halfWidthValue = convertToHalfWidthNumber(value)
    joinStore.formData.phone[index] = halfWidthValue
  }

  // 郵便番号入力時の処理
  const handlePostalCodeInput = async (event, index) => {
    // 全角を半角に変換
    const value = event.target.value
    const halfWidthValue = convertToHalfWidthNumber(value)
    joinStore.formData.postalCode[index] = halfWidthValue

    // 両方の入力が揃っているか確認
    if (joinStore.isPostalCodeFilled) {
      joinStore.setAddress(await regionStore.getAddressWithPostalCode(joinStore.postalCodeWithHyphen))
    }
  }

  // メールアドレス入力時：使えない文字を即座に除去
  const handleEmailInput = event => {
    // メールで使える文字以外を除去（a-zA-Z0-9._%+-@のみ許可）
    const filtered = event.target.value.replace(/[^a-zA-Z0-9._%+\-@]/g, '')

    // フィルタリング後の値をv-modelに反映
    joinStore.formData.email = filtered

    // input欄にも即時反映
    event.target.value = filtered
  }

  //////////////////////////////////////////
  // UI

  // 利用規約表示
  const showTerms = event => {
    event.preventDefault()
    showPopupTerms.value = true
  }

  // 利用規約同意
  const confirmTerms = () => {
    joinStore.formData.agreedTermsRevision = consts.terms().revision
    showPopupTerms.value = false
  }

  // 利用規約同意トグル
  const toggleTermsAgreement = () => {
    joinStore.toggleTermsAgreement(consts.terms().revision)
  }
  //////////////////////////////////////////

  // LIFECYCLE
  onMounted(async () => {
    try {
      // 都道府県情報を取得
      await regionStore.getPrefectures()
      // 利用規約
      termsValue.value = marked.parse(consts.terms().text)
    } catch (error) {}
  })

  // フォーム送信
  const onSubmit = async () => {
    if (!joinStore.isPasswordMatch()) {
      await dialogStore.showAlertDialog('エラー', 'パスワードが一致しません')
      return
    }

    // 登録API呼び出し
    const response = await joinStore.join()
    if (response) {
      // メール認証画面に遷移
      router.push('/joinmailsent')
    }
  }
</script>

<template>
  <header></header>
  <main class="join-main">
    <div class="join-content">
      <form @submit.prevent="onSubmit">
        <h1><img class="logo" src="@/assets/img/login_logo.svg" alt="ディーモ for 社労士" /></h1>
        <div class="form-grid">
          <div>
            <h2>
              法人番号
              <span class="required">必須</span>
            </h2>
            <input type="text" v-model="joinStore.formData.corporateNumber" placeholder="1234567890123" required maxlength="13" @input="handleCorporateNumberInput" />
          </div>
          <div>
            <h2>
              法人・団体の名称
              <span class="required">必須</span>
            </h2>
            <input id="company-name" type="text" v-model="joinStore.formData.companyName" v-autokana:company-name @input="handleFullWidthInput($event, 'companyName')" placeholder="法人・団体名を全角で入力してください" required />
          </div>
          <div>
            <h2>法人・団体の名称フリガナ</h2>
            <input id="company-name-kana" type="text" v-model="joinStore.formData.companyNameKana" @input="handleFullWidthInput($event, 'companyNameKana')" placeholder="法人・団体名を全角カタカナで入力してください" />
          </div>
          <div class="name-fields">
            <h2>
              代表者氏名
              <span class="required">必須</span>
            </h2>
            <div class="name-grid">
              <input id="last-name" type="text" v-model="joinStore.formData.lastName" v-autokana:last-name @input="handleFullWidthInput($event, 'lastName')" placeholder="姓" required />
              <input id="first-name" type="text" v-model="joinStore.formData.firstName" v-autokana:first-name @input="handleFullWidthInput($event, 'firstName')" placeholder="名" required />
            </div>
          </div>
          <div class="name-fields">
            <h2>代表者氏名フリガナ</h2>
            <div class="name-grid">
              <input id="last-name-kana" type="text" v-model="joinStore.formData.lastKana" @input="handleFullWidthInput($event, 'lastKana')" placeholder="セイ" />
              <input id="first-name-kana" type="text" v-model="joinStore.formData.firstKana" @input="handleFullWidthInput($event, 'firstKana')" placeholder="メイ" />
            </div>
          </div>
          <div>
            <h2>
              メールアドレス
              <span class="required">必須</span>
            </h2>
            <input type="email" v-model="joinStore.formData.email" required placeholder="メールアドレスを半角英数字で入力してください" autocomplete="username" @input="handleEmailInput" />
          </div>
          <div>
            <h2>
              パスワード
              <span class="required">必須</span>
            </h2>
            <input type="password" v-model="joinStore.formData.password" required minlength="8" placeholder="8文字以上で入力してください" autocomplete="new-password" />
          </div>
          <div>
            <h2>
              パスワード（確認用）
              <span class="required">必須</span>
            </h2>
            <input type="password" v-model="joinStore.formData.passwordConfirm" required minlength="8" placeholder="パスワードを再入力してください" autocomplete="new-password" />
          </div>
          <div>
            <h2>
              郵便番号
              <span class="required">必須</span>
            </h2>
            <div class="postal-code-grid">
              <input type="text" v-model="joinStore.formData.postalCode[0]" placeholder="123" maxlength="3" required @input="handlePostalCodeInput($event, 0)" />
              <span class="hyphen">-</span>
              <input type="text" v-model="joinStore.formData.postalCode[1]" placeholder="4567" maxlength="4" required @input="handlePostalCodeInput($event, 1)" />
            </div>
          </div>
          <div>
            <h2>
              都道府県
              <span class="required">必須</span>
            </h2>
            <div class="select">
              <select v-model="joinStore.formData.prefecture" required>
                <option value="">選択してください</option>
                <option v-for="pref in regionStore.prefectureNames" :key="pref" :value="pref">
                  {{ pref }}
                </option>
              </select>
            </div>
          </div>
          <div>
            <h2>
              市区町村、行政区及び町名番地
              <span class="required">必須</span>
            </h2>
            <input id="address1" type="text" v-model="joinStore.formData.address1" v-autokana:address1 placeholder="全角で入力してください" @input="handleFullWidthInput($event, 'address1')" required />
          </div>
          <div>
            <h2>市区町村、行政区及び町名番地フリガナ</h2>
            <input id="address1-kana" type="text" v-model="joinStore.formData.address1Kana" placeholder="全角カタカナで入力してください" @input="handleFullWidthInput($event, 'address1Kana')" />
          </div>
          <div>
            <h2>ビル名、建物名、マンション名等</h2>
            <input id="address2" type="text" v-model="joinStore.formData.address2" v-autokana:address2 placeholder="全角で入力してください" @input="handleFullWidthInput($event, 'address2')" />
          </div>
          <div>
            <h2>ビル名、建物名、マンション名等フリガナ</h2>
            <input id="address2-kana" type="text" v-model="joinStore.formData.address2Kana" placeholder="全角カタカナで入力してください" @input="handleFullWidthInput($event, 'address2Kana')" />
          </div>
          <div>
            <h2>
              電話番号
              <span class="required">必須</span>
            </h2>
            <div class="phone-grid">
              <input type="text" v-model="joinStore.formData.phone[0]" placeholder="012" maxlength="5" required @input="handlePhoneInput($event, 0)" />
              <span class="hyphen">-</span>
              <input type="text" v-model="joinStore.formData.phone[1]" placeholder="3456" maxlength="4" required @input="handlePhoneInput($event, 1)" />
              <span class="hyphen">-</span>
              <input type="text" v-model="joinStore.formData.phone[2]" placeholder="7890" maxlength="4" required @input="handlePhoneInput($event, 2)" />
            </div>
          </div>
          <div class="terms-checkbox">
            <label>
              <input type="checkbox" :checked="joinStore.isAgreeToTerms" @change="toggleTermsAgreement" />
              <a href="#" @click="showTerms">利用規約</a>
              に同意する
            </label>
          </div>
          <button type="submit" class="input-submit" :disabled="!joinStore.isFormValid">登録する</button>
        </div>
      </form>
    </div>
  </main>
  <FooterNoneLogin />

  <EditDialog :hide-close-button="true" :show-copy-button="false" :show-delete-button="false" v-model:visible="showPopupTerms" @save="confirmTerms" save-button-text="同意する">
    <template v-slot:title>ディーモ for 社労士利用規約</template>
    <template v-slot:content>
      <div class="markdown scroller" v-html="termsValue"></div>
    </template>
  </EditDialog>
</template>
<style lang="less" scoped></style>
